/**
 * components/landing/ai-platform/sections/PromptIntelligence.jsx — §4.6.
 *
 * Six-tile capability grid.  Pairs it with a mini prompt-anatomy card
 * that shows what actually gets sent to the model — system, tools,
 * context, user.
 */

import React from 'react'
import {
  Wand2, FileText, ArrowDownToDot, Brain, Braces, GitBranch, Sparkles, Code2,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { PROMPT_CAPS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Wand2, FileText, ArrowDownToDot, Brain, Braces, GitBranch }

export default function PromptIntelligence() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Prompt Intelligence</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Every prompt is a <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">reproducible artefact.</span></>}
            subtitle="Prompt orchestration is the boring part of AI — versioning, context injection, tool schemas, memory. We take it seriously so your AI stays predictable, testable and safe."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Capabilities — 3 col */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PROMPT_CAPS.map((c) => {
              const tone = TONE[c.tone] || TONE.orange
              const Icon = ICONS[c.iconKey] || Sparkles
              return (
                <div key={c.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.03] transition-all">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon style={{ width: 14, height: 14 }} strokeWidth={1.8} />
                    </span>
                    <h4 className="text-[13.5px] font-semibold text-white leading-tight">{c.label}</h4>
                  </div>
                  <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-2">{c.body}</p>
                </div>
              )
            })}
          </div>

          {/* Prompt anatomy — 2 col */}
          <div className="lg:col-span-2">
            <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 backdrop-blur-xl overflow-hidden relative h-full">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.10), transparent 70%)' }} />
              <div className="relative flex flex-col gap-2.5">
                <div className="flex items-center gap-2">
                  <Code2 className="h-4 w-4 text-brand-gold" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-gold">Prompt anatomy · mentor-v14</span>
                </div>
                <PromptLine label="system" tone="blue">You are the Kalki Ai Cloud mentor. Cite sources. Never invent modules.</PromptLine>
                <PromptLine label="tools" tone="purple">open_module, run_scan, backtest_strategy, invite_teammate</PromptLine>
                <PromptLine label="context" tone="cyan">module=strategy, ticker=MES, role=trader, portfolio=live</PromptLine>
                <PromptLine label="retrieved" tone="orange">[6 chunks] regime-shift-guide · kelly-sizing · mes-microstructure …</PromptLine>
                <PromptLine label="user" tone="green">Why is my MES strategy underperforming this week?</PromptLine>
                <div className="text-[10.5px] font-mono text-zinc-500 pt-1">Every field is versioned. A/B tested. Cost-tracked.</div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

function PromptLine({ label, tone, children }) {
  const t = TONE[tone] || TONE.orange
  return (
    <div className="rounded-lg border border-white/5 bg-black/40 p-2.5 font-mono text-[11.5px]">
      <div className="text-[9.5px] uppercase tracking-wider" style={{ color: t.hex }}>{label}</div>
      <div className="text-zinc-200 mt-0.5 leading-snug">{children}</div>
    </div>
  )
}
